import React, { useState } from 'react';
import Logo from '../mediaFiles/JPG-Logo.svg';
import { message } from 'antd';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { BASE_URL } from '../apis/constant';
import { Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await axios.post(`${BASE_URL}/token/`, {
        username: formData.username,
        password: formData.password
      }, {
        withCredentials: true
      });

      if (res.status === 200) {
        localStorage.setItem('access_token', res.data.access);
        localStorage.setItem('refresh_token', res.data.refresh);
        messageApi.success('Login successful !');
        navigate("/admin/orders", { replace: true });
      }

    } catch (error) {
      console.error('Error during login:', error);
      messageApi.error('Wrong Credentials !');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .login-container {
          min-height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .login-card {
          background: white;
          border-radius: 12px;
          padding: 32px 28px;
          width: 100%;
          max-width: 360px;
          box-shadow: 
            0 2px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        .login-header {
          text-align: center;
          margin-bottom: 28px;
        }

        .logo-container {
          width: 60px;
          height: 60px;
          margin: 0 auto 16px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-container img {
          width: 100%;
          height: auto;
        }

        .login-header h1 {
          font-size: 22px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 6px;
          letter-spacing: -0.3px;
        }

        .login-header p {
          color: #666;
          font-size: 14px;
          font-weight: 400;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .input-group {
          position: relative;
        }

        .input-group input {
          width: 100%;
          padding: 14px 16px;
          border: 1.5px solid #e1e5e9;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s ease;
          outline: none;
          color: #1a1a1a;
        }

        .input-group input:focus {
          border-color: #1a1a1a;
          background: #fafafa;
          box-shadow: 0 0 0 2px rgba(26, 26, 26, 0.1);
        }

        .input-group input::placeholder {
          color: transparent;
        }

        .input-group input:focus + label,
        .input-group input:not(:placeholder-shown) + label {
          top: -6px;
          left: 10px;
          font-size: 11px;
          color: #1a1a1a;
          background: white;
          padding: 0 4px;
          font-weight: 500;
        }

        .input-group label {
          position: absolute;
          top: 14px;
          left: 14px;
          font-size: 14px;
          color: #888;
          transition: all 0.2s ease;
          pointer-events: none;
          background: transparent;
          font-weight: 400;
        }

        .password-input {
          padding-right: 48px !important;
        }

        .password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #888;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px;
          transition: color 0.2s ease;
        }

        .password-toggle:hover {
          color: #1a1a1a;
        }

        .login-button {
          background: #1a1a1a;
          color: white;
          border: none;
          padding: 14px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 4px;
        }

        .login-button:hover:not(:disabled) {
          background: #2a2a2a;
          transform: translateY(-1px);
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
        }

        .login-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 480px) {
          .login-card {
            padding: 28px 20px;
            margin: 10px;
          }
        }
      `}</style>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-container">
              <img src={Logo} alt="Logo" />
            </div>
            <h1>Welcome Back</h1>
            <p>Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <label>Username</label>
            </div>

            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="password-input"
                value={formData.password}
                onChange={handleChange}
                placeholder=" "
                required
              />
              <label>Password</label>
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginPage;